const DEFAULT_CACHE_DURATION = 60 * 60 * 1000; // 1 óra

/* Per-URL metadata of the last answer: when the data was fetched and whether
   it came from an expired cache entry (network failed). The data cards read
   this for their "Forrás: X · frissítve hh:mm" / "gyorsítótár" footer. */
const _meta = new Map();

export function getFetchMeta(url) {
  return _meta.get(url) || null;
}

/* The cached entry regardless of age, or null. Lets the app start from the
   last known data while the network request is still running. */
export function readCache(url) {
  try {
    const cached = localStorage.getItem(`api_cache_${url}`);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    return { data, timestamp };
  } catch (_) {
    return null;
  }
}

function makeAbortSignal(ms) {
  // AbortSignal.timeout() is not available in Safari < 16 or older browsers
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, cleanup: () => clearTimeout(id) };
}

export async function fetchWithCache(url, options = {}) {
  const cacheKey = `api_cache_${url}`;
  const maxRetries = options.retries ?? 3;
  const retryDelay = options.retryDelay ?? 2000;
  const cacheDuration = options.cacheDuration ?? DEFAULT_CACHE_DURATION;
  const transform = options.transform;

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < cacheDuration) {
        _meta.set(url, { at: timestamp, stale: false });
        return data;
      }
    }
  } catch (_) {}

  /* Retry policy:
     - 429: no retry. Hammering a rate-limited API only extends the block.
       The error carries status 429 so the UI can say "túlterhelt".
     - TypeError ("Failed to fetch"): no retry either. In the browser a 429
       without CORS headers (CoinGecko does this) surfaces as a CORS failure,
       i.e. a TypeError — so it is treated as the same "overloaded" case.
     - Other 4xx: no retry (the request itself is wrong).
     - Timeouts and 5xx: exponential backoff (retryDelay, 2×, 4×…). */
  let lastError;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (attempt > 0) {
      await new Promise(r => setTimeout(r, retryDelay * 2 ** (attempt - 1)));
    }

    const { signal, cleanup } = makeAbortSignal(8000);
    try {
      const response = await fetch(url, { signal });
      cleanup();

      if (!response.ok) {
        const err = new Error(response.status === 429 ? 'Rate limit' : `HTTP ${response.status}`);
        err.status = response.status;
        if (response.status === 429) err.overloaded = true;
        throw err;
      }

      let data = await response.json();
      if (transform) data = transform(data);

      try {
        localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
      } catch (_) {}

      _meta.set(url, { at: Date.now(), stale: false });
      return data;
    } catch (error) {
      cleanup();
      if (error instanceof TypeError) error.overloaded = true;
      lastError = error;
      console.warn(`API hívás hiba (${attempt + 1}/${maxRetries}):`, url, error.message);
      if (error.overloaded) break;
      if (error.status >= 400 && error.status < 500) break;
    }
  }

  // Stale cache fallback
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      console.warn('Lejárt cache-t használunk:', url);
      const { data, timestamp } = JSON.parse(cached);
      _meta.set(url, { at: timestamp, stale: true });
      return data;
    }
  } catch (_) {}

  // No data at all: forget any earlier meta, so no footer shows an old time.
  _meta.delete(url);
  throw lastError || new Error('API hívás sikertelen');
}

export function clearApiCache() {
  Object.keys(localStorage)
    .filter(k => k.startsWith('api_cache_'))
    .forEach(k => localStorage.removeItem(k));
  console.log('API cache törölve');
}
